import { g } from '@hcie/core';

/**
 * @file red_eye.ts
 * @description Red Eye Removal Tool implementation.
 * Processes an area under the brush to detect and diminish "redness" in eyes.
 */

/**
 * @function drawRedEye
 * @description Removes red-eye effect by processing pixels in a circular area with feathering/hardness.
 * @param {CanvasRenderingContext2D} sourceCtx - The source context to read original colors from.
 * @param {CanvasRenderingContext2D} destCtx - The target buffer context to write modified pixels to.
 * @param {number} x - Center X coordinate.
 * @param {number} y - Center Y coordinate.
 * @param {number} radius - Brush radius.
 * @param {number} hardness - Brush hardness (0.0 to 1.0) for soft edges.
 */
export function drawRedEye(
  sourceCtx: CanvasRenderingContext2D,
  destCtx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  hardness: number = 0.8
): void {
  if (!destCtx) return;
  const size: number = Math.ceil(radius * 2);
  const startX: number = Math.floor(x - radius);
  const startY: number = Math.floor(y - radius);
    
  try {
    const imageData: ImageData = sourceCtx.getImageData(startX, startY, size, size);
    const data: Uint8ClampedArray = imageData.data;
    
    const destImageData: ImageData = destCtx.getImageData(startX, startY, size, size);
    const destData: Uint8ClampedArray = destImageData.data;

    let isModified: boolean = false;

    for (let i = 0; i < data.length; i += 4) {
      const px: number = (i / 4) % size;
      const py: number = Math.floor((i / 4) / size);
      
      const dx: number = px - radius;
      const dy: number = py - radius;
      const distSq: number = dx * dx + dy * dy;
      const radSq: number = radius * radius;
      
      if (distSq > radSq) continue;

      const dist: number = Math.sqrt(distSq);
      let weight: number = 1.0;
      if (hardness < 1.0) {
        const featherStart: number = radius * hardness;
        if (dist > featherStart) {
          weight = 1.0 - (dist - featherStart) / (radius - featherStart);
        }
      }
      if (weight <= 0) continue;

      const red: number = data[i];
      const green: number = data[i + 1];
      const blue: number = data[i + 2];
      const alpha: number = data[i + 3];

      if (alpha === 0) continue;

      // Detection: Calculate redness relative to green and blue
      const avgGreenBlue: number = (green + blue) / 2;
      const rednessRatio: number = red / Math.max(1, avgGreenBlue);

      if (rednessRatio > 1.2 && red > 32) {
        const targetRed: number = avgGreenBlue;
        const newRed: number = red + (targetRed - red) * weight;
        
        destData[i] = newRed;
        destData[i + 1] = green;
        destData[i + 2] = blue;
        destData[i + 3] = alpha;
        isModified = true;
      }
    }

    if (isModified) {
      destCtx.putImageData(destImageData, startX, startY);
    }
  } catch (err) {
    // Expected during strokes outside bounds
  }
}

// Global Exposure
(window as any).drawRedEye = drawRedEye;
